import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    cyanMagentaFusionColorMapping,
    FusionColorMapping,
    grayscaleFusionColorMapping,
    selectedFusionColorMappingState,
} from '~/state/atoms/image/imageAtoms';
import { OverlayMenu, OverlayMenuOption } from '../../OverlayMenu';

const FUSION_COLOR_MAPPINGS: FusionColorMapping[] = [
    {
        id: 'orangeBlue',
        name: 'Orange–Blue',
        primaryValues: [1.0, 0.55, 0.0],
        secondaryValues: [0.0, 0.47, 1.0],
        previewPrimary: '#FF8C00',
        previewSecondary: '#0077FF',
    },
    {
        id: 'greenPurple',
        name: 'Green–Purple',
        primaryValues: [0.6, 1.0, 0.6],
        secondaryValues: [0.8, 0.6, 1.0],
        previewPrimary: '#99FF99',
        previewSecondary: '#CC99FF',
    },
    {
        id: 'yellowCyan',
        name: 'Yellow–Cyan',
        primaryValues: [1.0, 1.0, 0.6],
        secondaryValues: [0.6, 1.0, 1.0],
        previewPrimary: '#FFFF99',
        previewSecondary: '#99FFFF',
    },
    {
        id: 'fireIce',
        name: 'Fire–Ice',
        primaryValues: [1.0, 0.27, 0.0],
        secondaryValues: [0.0, 0.75, 1.0],
        previewPrimary: '#FF4500',
        previewSecondary: '#00BFFF',
    },
];

const allColorMappings = [
    grayscaleFusionColorMapping,
    cyanMagentaFusionColorMapping,
    ...FUSION_COLOR_MAPPINGS,
];

const ColorPreview = ({
    primary,
    secondary,
}: {
    primary: string;
    secondary: string;
}) => (
    <Box
        sx={{
            width: 12,
            height: 12,
            mr: 0.5,
            borderRadius: 1,
            background: `linear-gradient(to right, ${primary}, ${secondary})`,
            flexShrink: 0,
        }}
    />
);

const getDisplayName = (mapping: FusionColorMapping) =>
    mapping.id === 'noColorMapping' ? 'Grayscale' : mapping.name;

const fusionColorOptions: OverlayMenuOption<string>[] = allColorMappings.map(
    (mapping) => ({
        value: mapping.id,
        label: getDisplayName(mapping),
        icon: (
            <ColorPreview
                primary={mapping.previewPrimary}
                secondary={mapping.previewSecondary}
            />
        ),
    })
);

const FusionColorSelector = () => {
    const selectedFusionColorMapping = useRecoilValue(
        selectedFusionColorMappingState
    );
    const { updateSelectedFusionColorMapping } = useViewportEnvironment();

    const handleChange = (id: string) => {
        const mapping = allColorMappings.find((m) => m.id === id);
        if (mapping && mapping.id !== selectedFusionColorMapping?.id) {
            updateSelectedFusionColorMapping(mapping);
        }
    };

    const currentMapping =
        selectedFusionColorMapping ?? cyanMagentaFusionColorMapping;

    return (
        <OverlayMenu
            options={fusionColorOptions}
            value={currentMapping.id}
            onChange={handleChange}
            data-testid="fusion-color-selector"
            icon={
                <ColorPreview
                    primary={currentMapping.previewPrimary}
                    secondary={currentMapping.previewSecondary}
                />
            }
            renderLabel={() => getDisplayName(currentMapping)}
        />
    );
};

export default FusionColorSelector;
